import { createFileRoute, Link } from '@tanstack/react-router'
import { css } from '../../../../styled-system/css'
import { LoadingOverlay } from '../../../components/loading-overlay'
import { TablePaginationToolbar } from '../../../components/table-pagination-toolbar'
import { useGetAllFundSources, useGetFundSourceFilters } from '../../../libs/admin/hooks'
import { badge, badgeRow, badgeStack } from '../../../styles/badgeStyles'
import { button } from '../../../styles/buttonStyles'
import { contentWrapper } from '../../../styles/contentWrapper'
import { pageTitle, titleWrapper } from '../../../styles/sectionStyles'
import { table, tableCell, tableHead, tableHeadCell, tableRow, tableWrapper } from '../../../styles/tableStyles'
import { useState } from 'react'

export const Route = createFileRoute('/admin/fundSources/')({
  component: RouteComponent,
})

const fundSourceTypeLabels: Record<string, string> = {
  Distributable: '配布ポイント',
  StandaloneDistributable: '配布ポイント（外部）',
  AdminGrant: '管理コンソール発行ポイント',
}

const getFundSourceTypeLabel = (type: string) => {
  return fundSourceTypeLabels[type] ?? 'ミニアプリ連携ポイント'
}

const DATA_PER_PAGE = 15
type UsageBadge = {
  label: string
  color: 'orange' | 'blue' | 'green'
}

const UsageBadges = ({ fundSourceId }: { fundSourceId: string }) => {
  const { data: filters, isLoading } = useGetFundSourceFilters({ fundSourceId })
  const hasCities = (filters ?? []).some((filter) => Boolean(filter.availableCity))
  const hasStores = (filters ?? []).some((filter) => Boolean(filter.availableStoreId))
  const badges: UsageBadge[] = []

  if (!hasCities && !hasStores) {
    badges.push({ label: '全店舗', color: 'orange' })
  }
  if (hasCities) {
    badges.push({ label: '特定地域のみ', color: 'green' })
  }
  if (hasStores) {
    badges.push({ label: '一部店舗のみ', color: 'blue' })
  }

  if (isLoading) {
    return <span className={css({ color: 'text.secondary', fontSize: '14px' })}>-</span>
  }

  const containerClassName = badges.length > 1 ? badgeStack() : badgeRow()

  return (
    <div className={containerClassName}>
      {badges.map((usage) => (
        <span key={usage.label} className={badge({ color: usage.color })}>
          {usage.label}
        </span>
      ))}
    </div>
  )
}

const FundSourcesTable = () => {
  const { data, isFetching } = useGetAllFundSources()
  const fundSources = data ?? []
  const [page, setPage] = useState(0)
  const totalPages = Math.ceil(fundSources.length / DATA_PER_PAGE)
  const startIndex = page * DATA_PER_PAGE
  const endIndex = startIndex + DATA_PER_PAGE
  const paginatedData = fundSources.slice(startIndex, endIndex)

  return (
    <div className={tableWrapper()}>
      <TablePaginationToolbar
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />
      <table className={table()}>
        <thead className={tableHead()}>
          <tr>
            <th className={tableHeadCell()}>ポイント名</th>
            <th className={tableHeadCell()}>ポイントタイプ</th>
            <th className={tableHeadCell()}>譲渡</th>
            <th className={tableHeadCell()}>ポイント利用先</th>
            <th className={tableHeadCell()}>ID</th>
            <th className={tableHeadCell()} />
          </tr>
        </thead>
        <tbody>
          {paginatedData.map((fundSource) => (
            <tr className={tableRow()} key={fundSource.id}>
              <td className={tableCell()}>{fundSource.name}</td>
              <td className={tableCell()}>{getFundSourceTypeLabel(fundSource.type)}</td>
              <td className={tableCell()}>{fundSource.forbidTransfer ? '不可' : '可'}</td>
              <td className={css(tableCell.raw(), { padding: '8px 24px' })}>
                <UsageBadges fundSourceId={fundSource.id} />
              </td>
              <td className={tableCell()}>{fundSource.id}</td>
              <td className={tableCell()}>
                <div className={css({
                  display: 'flex',
                  justifyContent: 'flex-end',
                })}>
                  <Link to="/admin/fundSources/$fundSourceId/edit" params={{ fundSourceId: fundSource.id }}>
                    <button type="button" className={button()}>
                      利用先の設定
                    </button>
                  </Link>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {isFetching && <LoadingOverlay />}
    </div>
  )
}

function RouteComponent() {
  return (
    <div className={contentWrapper()}>
      <div className={titleWrapper()}>
        <h1 className={pageTitle()}>ポイント種類</h1>
        <div className={css({ flex: 1 })} />
        <Link to="/admin/fundSources/newFundSource">
          <button type="button" className={button()}>
            新規ポイント作成
          </button>
        </Link>
      </div>
      <FundSourcesTable />
    </div>
  )
}
